"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/AuthContext";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tag, Gift, AlertCircle, Award } from "lucide-react";
import api from "@/lib/api";

const PROMO_COPY = {
  title: "Promo Codes",
  subtitle: "Redeem a promo code for bonus credits (tokens).",
  placeholder: "Enter promo code",
  apply: "Apply Code",
  applying: "Applying…",
  redeem: "Redeem for tokens",
  redeeming: "Redeeming…",
  invalid: "That code is not valid.",
  applied: "Code valid.",
  redeemed: "Code redeemed!",
  history: "Your Redemptions",
  noRedemptions: "No redemptions yet.",
  bonusQuestions: "bonus question(s)",
  bonusDays: "bonus day(s)",
  redeemedOn: "Redeemed",
  signIn: "Sign in to redeem promo codes.",
};

interface ValidateRes {
  valid: boolean;
  reason?: string;
  discount_type?: string;
  discount_value?: number;
  original_amount?: number;
  discount_amount?: number;
  final_amount?: number;
  benefit_description?: string;
  promo_code_id?: string;
  promo_code?: string;
}

interface Redemption {
  id: string;
  code: string;
  discount_type: string;
  benefit_description: string;
  discount_amount: number | null;
  bonus_questions: number | null;
  bonus_days: number | null;
  monnify_transaction_ref: string | null;
  redeemed_at: string | null;
}

export default function PromoPage() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [validation, setValidation] = useState<ValidateRes | null>(null);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (!user) return;
    const run = async () => {
      setLoadingHistory(true);
      try {
        const res = await api.getPromoRedemptions();
        setRedemptions(Array.isArray(res) ? res : []);
      } catch {
        toast.error("Failed to load promo history.");
      } finally {
        setLoadingHistory(false);
      }
    };
    void run();
  }, [user]);

  // Push signed-out visitors to sign in (but let the auth-gate resolve first).
  useEffect(() => {
    if (!authLoading && !user) {
      router.replace(`/auth/login?redirect=${encodeURIComponent("/promo")}`);
    }
  }, [authLoading, user, router]);

  const handleApply = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return toast.error("Enter a promo code.");
    setApplying(true);
    try {
      const res: ValidateRes = await api.validatePromo({
        code: trimmed,
        // "standalone" is the only product type the backend accepts for a
        // no-purchase token redemption (top up/exam pass/premium/standalone).
        // "subscription" is NOT a recognised product and made every code fail
        // validation with "Invalid purchase type".
        purchase_type: "standalone",
        purchase_amount: 0,
      });
      setValidation(res);
      if (res?.valid) {
        toast.success(res.benefit_description || PROMO_COPY.applied);
      } else {
        toast.error(res?.reason || PROMO_COPY.invalid);
      }
    } catch {
      setValidation(null);
      toast.error(PROMO_COPY.invalid);
    } finally {
      setApplying(false);
    }
  };

  const handleRedeem = async () => {
    if (!validation?.valid) return;
    const trimmed = code.trim().toUpperCase();
    setRedeeming(true);
    try {
      const res = await api.redeemPromo({
        code: trimmed,
        // Must match the validated product type ("standalone" for a no-purchase
        // token redemption; otherwise the backend rejects it).
        purchase_type: "standalone",
        purchase_amount_original: validation.original_amount ?? 0,
        purchase_amount_discounted: validation.final_amount ?? 0,
      });
      toast.success(
        `${PROMO_COPY.redeemed} +${res?.credits_added ?? 0} tokens (balance: ${res?.new_balance ?? 0}).`,
      );
      setCode("");
      setValidation(null);
      const updated = await api
        .getPromoRedemptions()
        .catch(() => ({ redemptions: [] }));
      setRedemptions(updated?.redemptions || []);
    } catch {
      toast.error("Could not redeem code. Please try again.");
    } finally {
      setRedeeming(false);
    }
  };

  if (authLoading) {
    return (
      <PageTransition>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-8 pb-20">
          <div className="space-y-3">
            <div className="h-8 w-48 bg-muted rounded animate-pulse" />
            <div className="h-4 w-72 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </PageTransition>
    );
  }

  if (!user) {
    return (
      <PageTransition>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <Gift className="w-8 h-8 mx-auto text-y" />
          <p className="mt-3 text-sm text-muted-foreground">
            {PROMO_COPY.signIn}
          </p>
          <a
            href="/auth/login?redirect=/promo"
            className="inline-block mt-3 text-sm font-black uppercase text-y"
          >
            Go to sign in
          </a>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 md:py-12 space-y-6 md:space-y-8 pb-20">
        {/* Header */}
        <div>
          <h1 className="display text-xl md:text-3xl uppercase tracking-tighter font-black flex items-center gap-2">
            <Tag className="w-5 h-5 md:w-6 md:h-6 text-y" /> {PROMO_COPY.title}
          </h1>
          <p className="mt-1 text-xs md:text-sm font-syne text-muted-foreground">
            {PROMO_COPY.subtitle}
          </p>
        </div>

        {/* Redeem box */}
        <div className="matte-metal p-4 md:p-6 rounded-2xl md:rounded-[2rem] border border-border space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder={PROMO_COPY.placeholder}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="uppercase font-black tracking-widest text-sm"
              maxLength={32}
            />
            <Button
              onClick={handleApply}
              disabled={applying || !code.trim()}
              size="sm"
              className="font-black uppercase tracking-widest text-xs md:text-sm"
            >
              {applying ? PROMO_COPY.applying : PROMO_COPY.apply}
            </Button>
          </div>

          {validation?.valid && (
            <div className="rounded-xl border border-y/30 bg-y/5 p-4 space-y-3">
              <p className="text-xs font-black uppercase text-y">
                {PROMO_COPY.applied}
              </p>
              {validation.benefit_description ? (
                <p className="text-sm text-foreground">
                  {validation.benefit_description}
                </p>
              ) : null}
              {validation.discount_type ? (
                <p className="text-[10px] md:text-xs text-muted-foreground">
                  {validation.discount_type}
                  {validation.discount_amount != null
                    ? ` · −${validation.discount_amount}`
                    : ""}
                </p>
              ) : null}
              <Button
                onClick={handleRedeem}
                disabled={redeeming}
                className="w-full font-black uppercase tracking-widest text-xs md:text-sm"
              >
                {redeeming ? PROMO_COPY.redeeming : PROMO_COPY.redeem}
              </Button>
            </div>
          )}

          {validation && !validation.valid ? (
            <p className="text-xs text-red-400 flex items-center gap-2">
              <AlertCircle size={14} />
              {validation.reason || PROMO_COPY.invalid}
            </p>
          ) : null}
        </div>

        {/* Redemption history */}
        <div className="matte-metal p-4 md:p-6 rounded-2xl md:rounded-[2rem] border border-border space-y-4">
          <h2 className="display text-base md:text-xl uppercase tracking-tighter font-black flex items-center gap-2">
            <Award className="w-4 h-4 md:w-5 md:h-5 text-y" />{" "}
            {PROMO_COPY.history}
          </h2>
          {loadingHistory ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : redemptions.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {PROMO_COPY.noRedemptions}
            </p>
          ) : (
            <ul className="space-y-2">
              {redemptions.map((r) => (
                <li
                  key={r.id}
                  className="p-3 rounded-xl border border-border bg-background text-xs"
                >
                  <div className="font-black text-foreground">{r.code}</div>
                  {r.benefit_description ? (
                    <div className="text-muted-foreground">
                      {r.benefit_description}
                    </div>
                  ) : null}
                  {r.bonus_questions != null || r.bonus_days != null ? (
                    <div className="text-muted-foreground">
                      {r.bonus_questions != null &&
                        `${r.bonus_questions} ${PROMO_COPY.bonusQuestions}`}
                      {r.bonus_questions != null && r.bonus_days != null
                        ? " · "
                        : ""}
                      {r.bonus_days != null &&
                        `${r.bonus_days} ${PROMO_COPY.bonusDays}`}
                    </div>
                  ) : null}
                  {r.redeemed_at ? (
                    <div className="text-muted-foreground/60">
                      {PROMO_COPY.redeemedOn}: {r.redeemed_at}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </PageTransition>
  );
}
